<script setup lang="ts">
import type { Client } from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'

import { useClientsList } from '@/api'
import CheckIcon from '@/assets/icons/check.svg?component'
import ChevronIcon from '@/assets/icons/chevron.svg?component'
import { debounce } from '@/lib/helpers'
import { type FilterType } from '@/lib/types/common.types'
import { BoxComponent, FlexComponent, InputComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  advertiserGroupId?: number
  shouldTruncateText?: boolean
}>()
const client = defineModel<FilterType['client']>('client')
const query = ref('')
const label = computed(() => (client.value ? client.value.name || `ID: ${client.value.id}` : 'All clients'))
const { data, isLoading } = useClientsList(() => ({
  advertiserGroupId: props.advertiserGroupId,
  query: query.value,
  size: 20,
  sortBy: 'created_at:desc',
}))

const handleSearchQuery = debounce((value: string) => (query.value = value), 300)
const handleVisibleChange = (isOpen: boolean) => (isOpen ? (query.value = '') : void 0)
const handleClick = (item: Client) => (client.value = item.id === client.value?.id ? undefined : { id: item.id, name: item.name })

watch(
  () => props.advertiserGroupId,
  () => (query.value = ''),
)
</script>
<template>
  <el-dropdown trigger="click" @visible-change="handleVisibleChange">
    <BoxComponent :p="10" :min-w="129">
      <FlexComponent :gap="8" align="center" justify="space-between">
        <TypographyComponent variant="label-large">
          {{ label }}
        </TypographyComponent>
        <el-icon :size="18" class="chevron-icon">
          <ChevronIcon />
        </el-icon>
      </FlexComponent>
    </BoxComponent>
    <template #dropdown>
      <BoxComponent :pt="8" :pb="8">
        <BoxComponent :mt="8" :mb="16" :pr="16" :pl="16">
          <InputComponent :model-value="query" size="small" placeholder="Search clients" @input="handleSearchQuery" />
        </BoxComponent>
        <BoxComponent v-loading="isLoading" :min-h="35">
          <el-dropdown-menu v-if="data.length > 0">
            <el-dropdown-item v-for="item in data" :key="item.id" @click="handleClick(item)">
              <FlexComponent flex="1" justify="space-between">
                <TypographyComponent :variant="item.id === client?.id ? 'title-large' : 'body-large'">
                  {{ item.name || `ID: ${item.id}` }}
                </TypographyComponent>
                <el-icon v-if="item.id === client?.id" :size="24">
                  <CheckIcon />
                </el-icon>
              </FlexComponent>
            </el-dropdown-item>
          </el-dropdown-menu>
          <BoxComponent v-if="!isLoading && data.length === 0" :pt="10" :pb="10">
            <TypographyComponent is="p" variant="body-small" text-align="center">No matching data</TypographyComponent>
          </BoxComponent>
        </BoxComponent>
      </BoxComponent>
    </template>
  </el-dropdown>
</template>
<style lang="scss" scoped>
:deep(.el-dropdown-menu) {
  max-height: 440px;
  max-width: 300px;
  overflow: auto;
}
:deep(.el-dropdown-menu__item) {
  padding: 16px;
}
:deep(.el-input--small .el-input__wrapper) {
  padding: 8px;
}
</style>
